/**
 * TextAreaField Component - Multi-line text input
 */

import React, { useCallback, useRef, useEffect } from 'react';
import { TextAreaFieldProps } from '../../../types/forms';
import { BaseField } from './BaseField';

export const TextAreaField: React.FC<TextAreaFieldProps> = ({
  name,
  label,
  placeholder,
  helpText,
  required = false,
  disabled = false,
  readonly = false,
  error,
  className = '',
  value,
  rows = 4,
  maxLength,
  autoResize = false,
  showCount = false,
  onChange,
  onBlur,
  onFocus,
}) => {
  const fieldId = `field-${name}`;
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea based on content
  const adjustHeight = useCallback(() => {
    if (autoResize && textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [autoResize]);

  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;

      // Enforce max length if specified
      if (maxLength && newValue.length > maxLength) {
        return;
      }

      onChange?.(newValue);
      adjustHeight();
    },
    [onChange, maxLength, adjustHeight]
  );

  const textareaClasses = `
    w-full px-3 py-2
    border rounded-md shadow-sm
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
    ${error ? 'border-red-300' : 'border-gray-300'}
    ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
    ${readonly ? 'bg-gray-50' : ''}
    ${autoResize ? 'resize-none overflow-hidden' : 'resize-y'}
    transition-colors duration-200
  `;

  return (
    <BaseField
      name={name}
      label={label}
      helpText={helpText}
      required={required}
      disabled={disabled}
      readonly={readonly}
      error={error}
      className={className}
      fieldId={fieldId}
    >
      <div className="relative">
        <textarea
          ref={textareaRef}
          id={fieldId}
          name={name}
          value={value || ''}
          placeholder={placeholder}
          rows={rows}
          maxLength={maxLength}
          disabled={disabled}
          readOnly={readonly}
          required={required}
          className={textareaClasses}
          onChange={handleChange}
          onBlur={onBlur}
          onFocus={onFocus}
          aria-invalid={!!error}
          aria-describedby={error ? `${fieldId}-error` : undefined}
        />
        
        {showCount && (
          <div className="absolute bottom-2 right-2 text-xs text-gray-500">
            {value?.length || 0}
            {maxLength && `/${maxLength}`}
          </div>
        )}
      </div>
    </BaseField>
  );
};

export default TextAreaField;